import Link from 'next/link'
import { type MouseEvent, type MouseEventHandler, useCallback } from 'react'
import { useNavigation } from '~/context/NavigationProvider'
import {
  IButtonFields,
  INavigationElementFields,
} from '~/types/generated/contentful'

import {
  IReducedButton,
  IReducedNavigationElement,
} from '../types/reducedTypes'

type NavigationActionProps = {
  as?: typeof Link
  href?: string | null
  target?: string
  rel?: string
  onClick?: MouseEventHandler<HTMLButtonElement>
}

const useNavigationActions = (
  fields:
    | IReducedNavigationElement['fields']
    | INavigationElementFields
    | IButtonFields
    | IReducedButton['fields'],
): NavigationActionProps => {
  let type = fields.type

  if ('navigationType' in fields) {
    type = fields.navigationType
  }

  const actions: NavigationActionProps = {
    href: '/',
  }

  const { findFullUrlFromId } = useNavigation()

  const handleJumpToClick = useCallback(
    (e: MouseEvent<HTMLButtonElement, globalThis.MouseEvent>) => {
      e.preventDefault()
      if (fields.scrollToContentReference) {
        const targetElement = document.getElementById(
          fields.scrollToContentReference.sys.id,
        )

        if (!targetElement) {
          return
        }

        targetElement.tabIndex = -1
        targetElement?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        })

        setTimeout(() => {
          targetElement.focus() // Use setTimeout to delay the focus
        }, 1000) // Delay can be adjusted
      }
    },
    [fields],
  )

  if (!type) {
    return actions
  }

  if (type === 'Internal' && fields.redirectReference) {
    actions.as = Link

    if (fields.redirectReference.sys.id) {
      actions.href = findFullUrlFromId(fields.redirectReference.sys.id) ?? ''
    } else {
      actions.href = '/'
    }
  } else if (type === 'External' && fields.externalUrl) {
    actions.as = Link
    actions.href = fields.externalUrl ?? ''
    actions.target = '_blank'
    actions.rel = 'noopener noreferrer'
  } else if (type === 'File' && fields.fileReference) {
    actions.as = Link
    actions.href = fields.fileReference.fields?.id
      ? `api/file-download/${fields.fileReference.fields.id}`
      : ''
    actions.target = '_blank'
    actions.rel = 'noopener noreferrer'
  } else if (type === 'Jump To') {
    actions.as = 'a' as unknown as typeof Link
    actions.href = `#${fields.scrollToContentReference?.sys.id}` // Fragment identifier
    actions.onClick = handleJumpToClick
  }

  if (fields.openInNewTabOverride) {
    actions.target = '_blank'
    actions.rel = 'noopener noreferrer'
  }

  return actions
}

export default useNavigationActions
